import {
  formatDayLabel,
  formatStartsIn,
  formatTimeRange,
  upNext,
} from "../../helpers/calendar";
import { colors } from "../../style";
import { Btn, EmptyState, SectionLabel, StatusDot } from "../ui";
import { WhereLabel, eventColor, eventTitle, isDeclined } from "./common";

const ellipsis = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// Upcoming events grouped by day, with the meeting on now or up next on top
function CalendarAgenda({ events, now, onOpen }) {
  const next = upNext(events, now);
  const sorted = [...events].sort((a, b) => a.start - b.start);
  const groups = [];
  for (const e of sorted) {
    // Under way since an earlier day: list under today
    const label = formatDayLabel(
      e.start.isBefore(now, "day") ? now : e.start,
      now,
    );
    if (groups.at(-1)?.label !== label) groups.push({ label, items: [] });
    groups.at(-1).items.push(e);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {next && <UpNext event={next} now={now} onOpen={onOpen} />}

      {groups.length === 0 && (
        <EmptyState icon="calendar" title="Nothing coming up">
          Events from your calendar for the next 7 days show here.
        </EmptyState>
      )}

      {groups.map((g) => (
        <section key={g.label}>
          <SectionLabel as="h3" style={{ padding: "0 10px 6px" }}>
            {g.label}
          </SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {g.items.map((e) => (
              <AgendaRow
                key={`${e.id}-${e.start.valueOf()}`}
                event={e}
                onOpen={onOpen}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function AgendaRow({ event, onOpen }) {
  return (
    <button
      type="button"
      className="sk-row"
      onClick={() => onOpen(event)}
      style={{
        display: "flex",
        gap: "10px",
        width: "100%",
        padding: "9px 10px",
        border: 0,
        borderRadius: "10px",
        background: "none",
        color: colors.textStrong,
        font: "inherit",
        textAlign: "left",
        cursor: "pointer",
        opacity: isDeclined(event) ? 0.5 : 1,
      }}
    >
      <span
        style={{
          flex: "none",
          width: "3px",
          alignSelf: "stretch",
          borderRadius: "2px",
          background: eventColor(event),
        }}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            ...ellipsis,
            fontSize: "14px",
            fontWeight: 600,
            color: colors.text,
            textDecoration: isDeclined(event) ? "line-through" : undefined,
          }}
        >
          {eventTitle(event)}
        </span>
        <span
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "2px 10px",
            marginTop: "2px",
            fontSize: "12px",
            color: colors.textMuted,
          }}
        >
          <span>{formatTimeRange(event)}</span>
          <WhereLabel event={event} />
        </span>
      </span>
    </button>
  );
}

function UpNext({ event, now, onOpen }) {
  const c = eventColor(event);
  const live = !event.start.isAfter(now);
  return (
    <div
      style={{
        padding: "14px 16px",
        borderRadius: "14px",
        border: `1px solid ${c}59`,
        background: `linear-gradient(120deg, ${c}29, ${colors.surface} 70%)`,
      }}
    >
      <div
        role="button"
        tabIndex={0}
        className="sk-link"
        onClick={() => onOpen(event)}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") &&
          (e.preventDefault(), onOpen(event))
        }
        style={{ cursor: "pointer", borderRadius: "6px" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: c,
          }}
        >
          {live && <StatusDot color={c} pulse />}
          {live ? "Happening now" : `Up next · ${formatStartsIn(event, now)}`}
        </div>
        <div
          style={{
            ...ellipsis,
            margin: "3px 0",
            fontSize: "16px",
            fontWeight: 700,
            color: colors.text,
          }}
        >
          {eventTitle(event)}
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "2px 10px",
            fontSize: "12px",
            color: colors.textMuted,
          }}
        >
          <span>{formatTimeRange(event)}</span>
          <WhereLabel event={event} />
        </div>
      </div>
      {event.join_url && (
        <Btn
          as="a"
          href={event.join_url}
          target="_blank"
          rel="noopener noreferrer"
          variant="primary"
          size="sm"
          icon="video"
          style={{ marginTop: "10px" }}
        >
          Join
        </Btn>
      )}
    </div>
  );
}

export default CalendarAgenda;
